<template>
  <div class="article-wrapper" :class="{ light: light }">
    <div class="article-card">
      <div v-if="article" class="article-img-holder">
        <PorchSvg v-if="isRequestImpactStory" :color="organizationDetails.darkBrandColor" />
        <TreePlantingSvg
          v-if="isContributionImpactStory"
          :color="organizationDetails.darkBrandColor"
        />
      </div>
      <div class="article-content">
        <h4 v-if="article" class="story-expander" @click="toggleStoryExpansion">
          {{ article.title }}
        </h4>

        <div v-if="storyExpanded" class="article-expanded-story">
          <transition v-if="article" name="slide-fade">
            <div class="article-expanded-story">
              {{ article.text }}
            </div>
          </transition>
        </div>

        <div v-else>
          <transition v-if="article" name="slide-fade">
            <div class="article-story">
              {{ article.text }}
            </div>
          </transition>
        </div>
      </div>
      <div class="article-button hidden-desktop">
        <!-- Read more button -->
        <button
          v-if="!storyExpanded"
          @click="toggleStoryExpansion"
          class="form-button white-button"
        >
          <span v-translate>Read more</span>
        </button>

        <!-- Primary action button -->
        <button v-if="storyExpanded" @click="$emit('click')" class="form-button form-button-160">
          <span>
            {{ buttonText }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ImpactStory from '@/services/impactStories'
import { analyticsMixin } from '@/services/googleAnalytics'
import PorchSvg from '@/views/svg/PorchSvg'
import TreePlantingSvg from '@/views/svg/TreePlantingSvg'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'ArticleCard',
  mixins: [analyticsMixin],
  components: {
    PorchSvg,
    TreePlantingSvg,
  },
  props: {
    header: {
      required: true,
      type: String,
    },
    subHeader: {
      required: true,
      type: String,
    },
    article: {
      required: false,
      type: Object,
    },
    buttonText: {
      required: true,
      type: String,
    },
    light: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      storyExpanded: false,
    }
  },
  methods: {
    toggleStoryExpansion() {
      // toggle and track CTA
      let storyType = this.article.storyType
      if (this.storyExpanded) {
        this.trackClickedCTA(`collapsed ${storyType}`)
        this.storyExpanded = false
      } else {
        this.trackClickedCTA(`expanded ${storyType}`)
        this.storyExpanded = true
      }
    },
  },
  computed: {
    ...mapState(['organizationDetails', 'user']),
    isRequestImpactStory() {
      return this.article.storyType === ImpactStory.REQUEST_STORY
    },
    isContributionImpactStory() {
      return this.article.storyType === ImpactStory.CONTRIBUTION_STORY
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/base';
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/grant-circles';
@import '@/styles/mixins/responsive';

.article-wrapper {
  display: flex;
  width: 100%;
  margin-bottom: 2.5rem;
  justify-content: center;

  &.light {
    .article-card,
    .article-content {
      background-color: white;
    }
  }
}
.article-header {
  height: 4rem;
  width: 100%;
  display: flex;
  align-items: center;

  h5 {
    margin: 0 0 0 1rem;
    font-weight: normal;
    font-size: 1.1rem;
  }
}
.article-card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--light-brand-color);

  @include mobile-screen {
    max-width: 500px;
  }

  @include tablet-screen {
    max-width: 80%;
    align-items: center;
  }

  @include desktop-screen {
    flex-direction: row;
    padding: 4rem;
  }
}
.article-img {
  width: 100%;
  margin: 0;
}
.article-img-holder {
  overflow: hidden;
  margin: 1rem 1rem 2rem 1rem;
  align-items: center;

  svg {
    border-radius: 3.75rem 0;
  }

  @include tablet-screen {
    width: 70%;
    margin-top: 2rem;
  }

  @include desktop-screen {
    width: 35%;
    margin: 0;
  }
}
.article-content {
  padding: 0rem 1rem;
  background: var(--light-brand-color);

  @include desktop-screen {
    width: 65%;
    padding-left: 4rem;
  }
}
.article-story {
  font-size: 1.25rem;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  line-height: 2rem;
  max-height: 8.75rem;
  text-overflow: ellipsis;
  overflow: hidden;

  @include desktop-screen {
    display: block;
    text-overflow: unset;
    overflow: visible;
    max-height: unset;
  }
}
.article-expanded-story {
  font-size: 1.25rem;
  line-height: 2rem;
}
.article-button {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;

  .button {
    width: 100%;
  }
}
.story-expander {
  cursor: pointer;
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;

  @include desktop-screen {
    text-align: left;
  }
}

/* Enter and leave animations for the expandable story. */
.slide-fade-enter-active {
  transition: all 0.5s ease;
}
.slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  transition: all 0.5s ease;
  transform: translateY(-10px);
  opacity: 0;
}
.divider {
  width: 100%;
  height: 1px;
  border: 1px solid $very-light-gray;
  margin-top: 1rem;
}
</style>
